import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSource } from '../../services/hooks/useSources';
import { updateSource } from '../../services/sourceService';
import SourceUrlsList from './SourceUrlsList';
import AddUrlsForm from './AddUrlsForm';
import ProcessSourceButton from './ProcessSourceButton';
import ResetStatusButton from './ResetStatusButton';
import { AuditLogsList } from '../audit/AuditLogsList';
import { SourceFilter } from '../../../../models';
import SourceForm from './SourceForm';
import ScraperConfigForm from './ScraperConfigForm';
import SourceFiltersSection from './SourceFiltersSection';
import { Button, Badge, Spinner, Alert, Card, CardContent, Tabs, Tab } from '../ui';
import { ArrowLeft } from '../ui/icons';

const SourceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  const [localFilters, setLocalFilters] = useState<SourceFilter[]>([]);
  const [localNotificationEmails, setLocalNotificationEmails] = useState<string[]>([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  const {
    data: source,
    isLoading,
    error,
    refetch
  } = useSource(id || '');

  useEffect(() => {
    if (source) {
      setLocalFilters(source.filters || []);
      setLocalNotificationEmails(source.notificationEmails || []);
    }
  }, [source]);

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
  };

  const handleSaveFilters = async () => {
    if (!source) return;

    setFiltersLoading(true);
    try {
      await updateSource(source.id, {
        name: source.name,
        sourceType: source.sourceType,
        sourceUrl: source.sourceUrl,
        searchResultsConfig: source.searchResultsConfig,
        description: source.description,
        scrapingSchedule: source.scrapingSchedule || '',
        agencyId: source.agencyId,
        domain: source.domain,
        urlFilterPattern: source.urlFilterPattern,
        keepQueryParams: source.keepQueryParams || '',
        filters: localFilters,
        js: source.js || [],
      });
      refetch();
    } catch (error) {
      console.error('Error updating filters:', error);
    } finally {
      setFiltersLoading(false);
    }
  };

  const handleSaveNotifications = async () => {
    if (!source) return;

    setNotificationsLoading(true);
    try {
      await updateSource(source.id, {
        name: source.name,
        sourceType: source.sourceType,
        sourceUrl: source.sourceUrl,
        searchResultsConfig: source.searchResultsConfig,
        description: source.description,
        scrapingSchedule: source.scrapingSchedule || '',
        agencyId: source.agencyId,
        domain: source.domain,
        urlFilterPattern: source.urlFilterPattern,
        keepQueryParams: source.keepQueryParams || '',
        notificationEmails: localNotificationEmails,
        js: source.js || [],
      });
      refetch();
    } catch (error) {
      console.error('Error updating notification emails:', error);
    } finally {
      setNotificationsLoading(false);
    }
  };

  const getStatusBadgeVariant = (status: string): 'warning' | 'error' | 'success' | 'default' => {
    switch (status) {
      case 'PROCESSING':
        return 'warning';
      case 'FAILED':
        return 'error';
      case 'IDLE':
        return 'success';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen-50">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Alert variant="error" className="mb-6">
          Error loading source: {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
        <Button variant="outline" onClick={() => navigate('/sources')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Sources
        </Button>
      </div>
    );
  }

  if (!source) {
    return (
      <div>
        <h5 className="text-xl font-semibold mb-4">Source not found</h5>
        <Button variant="outline" onClick={() => navigate('/sources')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Sources
        </Button>
      </div>
    );
  }

  const tabs: Tab[] = [
    { label: 'URLs' },
    { label: 'Details' },
    { label: 'Filters' },
    { label: 'Scraper' },
    { label: 'Notifications' },
    { label: 'Audit Logs' },
  ];

  return (
    <div>
      <div className="flex items-center mb-6">
        <Button
          variant="outline"
          onClick={() => navigate('/sources')}
          className="mr-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold">
            Source: {source.name}
            {source.processingStatus && (
              <Badge
                variant={getStatusBadgeVariant(source.processingStatus)}
                size="sm"
                className="ml-4 align-middle"
              >
                {source.processingStatus}
              </Badge>
            )}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Agency ID: {source.agencyId || 'Not specified'} • Type: {source.sourceType || 'URL_LIST'}
          </p>
        </div>
        <ProcessSourceButton
          sourceId={source.id}
          onProcessComplete={() => refetch()}
          disabled={source.processingStatus === 'PROCESSING'}
        />
        <div className="ml-2">
          <ResetStatusButton
            sourceId={source.id}
            sourceName={source.name}
            currentStatus={source.processingStatus || 'IDLE'}
            onResetComplete={() => refetch()}
            disabled={source.processingStatus === 'IDLE'}
          />
        </div>
      </div>

      {source.processingStatus === 'FAILED' && source.errorMessage && (
        <Alert variant="error" className="mb-6">
          <p className="font-semibold">Processing Error:</p>
          <p className="text-sm mt-1">{source.errorMessage}</p>
        </Alert>
      )}

      {source.processingStatus === 'PROCESSING' && (
        <Alert variant="warning" className="mb-6">
          <p className="font-semibold">Source is currently processing...</p>
          <p className="text-sm mt-1">
            If the source has been stuck in this state for a long time, you can use the "Reset Status" button to clear the processing state and try again.
          </p>
        </Alert>
      )}

      <Card className="mb-6">
        <CardContent>
          <Tabs tabs={tabs} activeTab={tabValue} onChange={handleTabChange} />

          {/* URLs Tab */}
          <div hidden={tabValue !== 0} className="mt-6">
            {tabValue === 0 && id && (
              <>
                {source.sourceUrl && (
                  <div className="mb-6">
                    <p className="text-sm text-gray-600 mb-2">
                      {source.sourceType === 'SITEMAP' && (
                        <>Sitemap URL: <strong>{source.sourceUrl}</strong>. Click the "Process Source" button to fetch and process URLs from the sitemap.</>
                      )}
                      {source.sourceType === 'SEARCH_RESULTS' && (
                        <>Search Results URL: <strong>{source.sourceUrl}</strong>. Click the "Process Source" button to scrape property links from search results.</>
                      )}
                    </p>
                    {source.sourceType === 'SEARCH_RESULTS' && source.searchResultsConfig && (
                      <div className="mt-3 space-y-1">
                        <p className="text-sm text-gray-600">
                          Property Link Selector: <strong>{source.searchResultsConfig.propertyLinkSelector}</strong>
                        </p>
                        {source.searchResultsConfig.nextPageSelector && (
                          <p className="text-sm text-gray-600">
                            Next Page Selector: <strong>{source.searchResultsConfig.nextPageSelector}</strong>
                          </p>
                        )}
                        <p className="text-sm text-gray-600">
                          Browser Mode:{' '}
                          <strong>
                            {source.searchResultsConfig.useBrowser
                              ? 'Headless browser enabled'
                              : 'Disabled'}
                          </strong>
                        </p>
                        {source.searchResultsConfig.maxPages && (
                          <p className="text-sm text-gray-600">
                            Max Pages: <strong>{source.searchResultsConfig.maxPages}</strong>
                          </p>
                        )}
                        {source.keepQueryParams && (
                          <p className="text-sm text-gray-600">
                            Keep Query Parameters: <strong>{source.keepQueryParams}</strong>
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <AddUrlsForm sourceId={id} onUrlsAdded={() => refetch()} />
                <hr className="my-6 border-gray-200" />
                <SourceUrlsList sourceId={id} />
              </>
            )}
          </div>

          {/* Details Tab */}
          <div hidden={tabValue !== 1} className="mt-6">
            {tabValue === 1 && (
              <SourceForm
                initialData={source}
                isEdit={true}
                onSave={() => refetch()}
              />
            )}
          </div>

          {/* Filters Tab */}
          <div hidden={tabValue !== 2} className="mt-6">
            {tabValue === 2 && (
              <>
                <SourceFiltersSection
                  filters={localFilters}
                  notificationEmails={localNotificationEmails}
                  onFiltersChange={setLocalFilters}
                  onNotificationEmailsChange={setLocalNotificationEmails}
                  showFiltersOnly={true}
                />
                <div className="mt-6 flex justify-end">
                  <Button
                    variant="primary"
                    onClick={handleSaveFilters}
                    disabled={filtersLoading}
                  >
                    {filtersLoading && <Spinner size="sm" className="mr-2" />}
                    {filtersLoading ? 'Saving...' : 'Save Filters'}
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Scraper Tab */}
          <div hidden={tabValue !== 3} className="mt-6">
            {tabValue === 3 && (
              <ScraperConfigForm sourceId={source.id} source={source} onSave={() => refetch()} />
            )}
          </div>

          {/* Notifications Tab */}
          <div hidden={tabValue !== 4} className="mt-6">
            {tabValue === 4 && (
              <>
                <SourceFiltersSection
                  filters={localFilters}
                  notificationEmails={localNotificationEmails}
                  onFiltersChange={setLocalFilters}
                  onNotificationEmailsChange={setLocalNotificationEmails}
                  showNotificationsOnly={true}
                />
                <div className="mt-6 flex justify-end">
                  <Button
                    variant="primary"
                    onClick={handleSaveNotifications}
                    disabled={notificationsLoading}
                  >
                    {notificationsLoading && <Spinner size="sm" className="mr-2" />}
                    {notificationsLoading ? 'Saving...' : 'Save Notifications'}
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Audit Logs Tab */}
          <div hidden={tabValue !== 5} className="mt-6">
            {tabValue === 5 && source && id && (
              <AuditLogsList sourceId={id} />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SourceDetail;
